import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { Button } from '@openng/optimus-ui/button';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { Select } from '@openng/optimus-ui/select';
import { TableModule } from '@openng/optimus-ui/table';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { CommercialContact } from 'src/app/model/commercial-services/commercial-contact';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

export interface CommercialFacilityContactsDialogData {
  /** Contacts currently assigned to the facility, in order. */
  assigned: CommercialContact[];
  /** All contacts that may be assigned. */
  available: CommercialContact[];
}

/**
 * Manage Contacts editor. Open with DialogService; the dialog closes with the
 * ordered contact ids when applied, or undefined when cancelled.
 */
@Component({
  selector: 'app-commercial-facility-contacts-dialog',
  templateUrl: './commercial-facility-contacts-dialog.component.html',
  styleUrls: ['./commercial-facility-contacts-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, Select, TableModule, Tooltip, TranslatePipe],
})
export class CommercialFacilityContactsDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(
    DynamicDialogConfig<CommercialFacilityContactsDialogData>
  );
  private readonly _logAndMessage = inject(LogAndMessageService);

  private readonly initialIds: string[] = (
    this.config.data?.assigned ?? []
  ).map((contact: CommercialContact) => contact.id);
  private readonly available: CommercialContact[] =
    this.config.data?.available ?? [];

  readonly draftContacts = signal<CommercialContact[]>([
    ...(this.config.data?.assigned ?? []),
  ]);
  readonly selectedContactId = signal<string | null>(null);

  readonly hasChanges = computed(() => {
    const draft = this.draftContacts();
    return (
      this.initialIds.length !== draft.length ||
      this.initialIds.some((id, index) => id !== draft[index].id)
    );
  });

  readonly pickerOptions = computed<SelectItem[]>(() => {
    const linkedIds = new Set(
      this.draftContacts().map((contact) => contact.id)
    );
    return this.available
      .filter((contact) => !contact.deletedAt && !linkedIds.has(contact.id))
      .map((contact) => ({
        label: contact.jobRole?.name
          ? `${this.fullName(contact)} · ${contact.jobRole.name}`
          : this.fullName(contact),
        value: contact.id,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  });

  fullName(contact: CommercialContact): string {
    return `${contact.firstName} ${contact.lastName}`.trim();
  }

  assignContact(): void {
    const contact = this.available.find(
      (item) => item.id === this.selectedContactId() && !item.deletedAt
    );
    if (
      !contact ||
      this.draftContacts().some((item) => item.id === contact.id)
    ) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'COMMERCIAL_SERVICES.MESSAGES.ERROR.CONTACT_UNAVAILABLE',
      });
      return;
    }
    this.draftContacts.update((contacts) => [...contacts, contact]);
    this.selectedContactId.set(null);
  }

  remove(contactId: string): void {
    this.draftContacts.update((contacts) =>
      contacts.filter((contact) => contact.id !== contactId)
    );
  }

  move(index: number, offset: -1 | 1): void {
    const target = index + offset;
    const contacts = [...this.draftContacts()];
    if (target < 0 || target >= contacts.length) {
      return;
    }
    [contacts[index], contacts[target]] = [contacts[target], contacts[index]];
    this.draftContacts.set(contacts);
  }

  onRowReorder(): void {
    // The table reorders in place; a new reference notifies signal dependents.
    this.draftContacts.update((contacts) => [...contacts]);
  }

  applyChanges(): void {
    this.ref.close(this.draftContacts().map((contact) => contact.id));
  }

  cancel(): void {
    this.ref.close();
  }
}
